/**
 * Das Nachrichtenfenster ist von überall in Kontor erreichbar (Benjamin,
 * 23.09.2026): die Shell hält es offen oder zu, jede Seite kann es öffnen —
 * auch gleich auf einen Kunden gefiltert oder auf ein Gespräch.
 *
 * Seit 24.09.2026 zwei Ergänzungen:
 * - **Öffnen ohne Ziel lässt den Stand stehen.** Das Fenster wird beim
 *   Schließen nur versteckt; wer es wieder öffnet, steht dort, wo er war
 *   (Gespräch, Filter, Suche, Scrollstand, angefangene Antwort). Nur ein
 *   ausdrückliches Ziel (`oeffnen({ … })`) springt woandershin.
 * - **Bezug der Seite.** Beleg-Editor, Belegdetail und Projektseite melden,
 *   um welchen Kunden und welches Projekt es gerade geht
 *   (`useNachrichtenBezug`). Das Fenster hebt dessen Gespräche nach oben.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

export type NachrichtenZiel = { kundeId?: string; kundeName?: string; schluessel?: string };
export type NachrichtenBezug = { kundeId?: string; kundeName?: string; projektId?: string; projektTitel?: string };

type Wert = {
  offen: boolean;
  ziel: NachrichtenZiel;
  /** Zählt jedes Öffnen MIT Ziel — nur dann setzt das Fenster seinen Stand neu. */
  zielNr: number;
  bezug: NachrichtenBezug | null;
  oeffnen: (ziel?: NachrichtenZiel) => void;
  schliessen: () => void;
  setBezug: (b: NachrichtenBezug | null) => void;
};

const Kontext = createContext<Wert | null>(null);

export function NachrichtenAnbieter({ children }: { children: ReactNode }) {
  const [offen, setOffen] = useState(false);
  const [ziel, setZiel] = useState<NachrichtenZiel>({});
  const [zielNr, setZielNr] = useState(0);
  const [bezug, setBezug] = useState<NachrichtenBezug | null>(null);
  const oeffnen = useCallback((z?: NachrichtenZiel) => {
    if (z) { setZiel(z); setZielNr((n) => n + 1); }
    setOffen(true);
  }, []);
  const schliessen = useCallback(() => setOffen(false), []);
  const wert = useMemo(
    () => ({ offen, ziel, zielNr, bezug, oeffnen, schliessen, setBezug }),
    [offen, ziel, zielNr, bezug, oeffnen, schliessen],
  );
  return <Kontext.Provider value={wert}>{children}</Kontext.Provider>;
}

/** Außerhalb der Shell (Harness, Tests) ein stiller Ersatz statt eines Absturzes. */
export function useNachrichtenFenster(): Wert {
  return useContext(Kontext) || {
    offen: false, ziel: {}, zielNr: 0, bezug: null, oeffnen: () => {}, schliessen: () => {}, setBezug: () => {},
  };
}

/**
 * Meldet der Shell, um welchen Kunden/welches Projekt es auf dieser Seite geht.
 * Beim Verlassen der Seite fällt der Bezug wieder weg.
 */
export function useNachrichtenBezug(b: NachrichtenBezug) {
  const { setBezug } = useNachrichtenFenster();
  const { kundeId = "", kundeName = "", projektId = "", projektTitel = "" } = b;
  useEffect(() => {
    setBezug(kundeId || projektId ? { kundeId, kundeName, projektId, projektTitel } : null);
  }, [setBezug, kundeId, kundeName, projektId, projektTitel]);
  useEffect(() => () => setBezug(null), [setBezug]);
}
